import { type TFunction } from 'next-i18next'

import { type ListingPrice, ListingPriceType } from '@/generated'

import FixedPriceAmount from './FixedPriceAmount'
import { isKeyInListingPrice } from './isKeyListingPrice'
import SpecifiedAmount from './SpecifiedAmount'

/**
 * Type definition for a set of price types for a listing.
 */
export type PriceTypes = {
  [key in
    | ListingPriceType.SpecifiedAmount
    | ListingPriceType.Fixed
    | ListingPriceType.GiveAway
    | ListingPriceType.Contact
    | ListingPriceType.SwapTrade
    | ListingPriceType.Free
    | ListingPriceType.MakeOffer
    | ListingPriceType.DriveAway]: {
    /**
     * Flag indicating if the price type has a value.
     */
    hasValue?: boolean
    /**
     * The React component that renders the price label for the price type.
     * @param {Object} props The component props.
     * @param {ListingPrice | undefined} props.amount The price amount to be displayed.
     * @param {boolean} [props.isFrench] A flag indicating if the price should be formatted for French locale (optional).
     */
    component: React.FC<{
      price?: ListingPrice | undefined
      isFrench?: boolean
      translate?: TFunction | undefined
    }>
  }
}

/**
 * An object representing a listing price amount with an optional flag indicating if the price should be formatted for French locale.
 */
export type ListingPriceAmount = {
  /**
   * The price amount to be displayed.
   */
  price?: ListingPrice | undefined
  /**
   * A flag indicating if the price should be formatted for French locale (optional).
   */
  isFrench?: boolean
  /**
   * A translation method.
   */
  translate?: TFunction
}

/**
 * Returns a JSX element based on the `priceType` parameter, which can be one of several price types.
 *
 * @param amount The listing price, of type `ListingPrice`.
 * @param priceType A string representing the type of price to display.
 * @param isFrench A boolean indicating whether to display the price in French.
 * @returns A JSX element displaying the price according to the specified price type.
 *
 * @example const priceElement = getPriceLabelValue({ amount: 42 }, SPECIFIED_AMOUNT, true);
 * console.log(priceElement); // Output: <span><span data-testid="shopping-ads-price">42,00 $</span> </span>
 *
 * const priceElement = getPriceLabelValue(amount, CONTACT, true);
 * console.log(priceElement); // Output: <span>Sur demande</span>
 */

export type getPriceLabelValueProps = {
  price: ListingPrice
  priceType: ListingPriceType
  isFrench?: boolean
  translation?: TFunction
}

export const GetPriceLabelValue = ({
  price,
  priceType,
  isFrench,
  translation,
}: getPriceLabelValueProps) => {
  const priceTypes: PriceTypes = {
    [ListingPriceType.SpecifiedAmount]: {
      hasValue: true,
      component: ({ price, isFrench }: ListingPriceAmount) =>
        price ? <SpecifiedAmount price={price} isFrench={isFrench} /> : null,
    },
    [ListingPriceType.Fixed]: {
      hasValue: true,
      component: ({ price, isFrench }: ListingPriceAmount) =>
        price ? <FixedPriceAmount price={price} isFrench={isFrench} /> : null,
    },
    [ListingPriceType.Contact]: {
      component: ({ translate }: ListingPriceAmount) =>
        translate ? (
          <span data-testid="shopping-ads-contact">{translate('listing.price.types.contact')}</span>
        ) : null,
    },
    [ListingPriceType.SwapTrade]: {
      component: ({ translate }: ListingPriceAmount) =>
        translate ? (
          <span data-testid="shopping-ads-swaptrade">
            {translate('listing.price.types.swap_trade')}
          </span>
        ) : null,
    },
    [ListingPriceType.Free]: {
      component: ({ translate }: ListingPriceAmount) =>
        translate ? (
          <span data-testid="shopping-ads-free">{translate('listing.price.types.give_away')}</span>
        ) : null,
    },
    [ListingPriceType.GiveAway]: {
      component: () => null,
    },
    [ListingPriceType.DriveAway]: {
      component: ({ price, isFrench }: ListingPriceAmount) =>
        price ? <FixedPriceAmount price={price} isFrench={isFrench} /> : null,
    },
    [ListingPriceType.MakeOffer]: {
      component: ({ price, isFrench }: ListingPriceAmount) =>
        price ? <FixedPriceAmount price={price} isFrench={isFrench} /> : null,
    },
  }

  if (isKeyInListingPrice(priceType, priceTypes)) {
    //when the price type match the ones available
    const PriceDetail = priceTypes[priceType].component

    return priceTypes[priceType]?.hasValue ? (
      <PriceDetail price={price} isFrench={isFrench} />
    ) : (
      <PriceDetail translate={translation} />
    )
  } else {
    return <></> //when the price type didnt match with the ones available
  }
}
